<page-heading
  [title]="'channel.list.pageTitle' | translate"
  [description]="'channel.list.pageDescription' | translate"
/>

@if (canSeeList()) {

  <!-- Controls: search (RF-14), sort (RF-04), view toggle (RF-05), refresh (RF-09), add -->
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-body health-controls">
          <div class="input-trend-search health-controls__search">
            <input
              type="search"
              #searchBox
              class="form-control"
              [placeholder]="'channel.list.searchPlaceholder' | translate"
              (keyup)="search(searchBox.value)"
            />
            <span class="mdi mdi-magnify search-icon ml-2"></span>
          </div>

          <!-- Sort select — same pattern as the Kanban funnel selector -->
          <button class="btn trend-button btn-primary fw-bold mdi mdi-sort dropdown-toggle health-controls__group"
            [matMenuTriggerFor]="sortMenu">
            {{ (sortMode === 'severity' ? 'channel.health.sort.severity' : 'channel.health.sort.name') | translate }}
          </button>
          <mat-menu #sortMenu="matMenu">
            <button mat-menu-item (click)="setSort('severity')">
              {{ 'channel.health.sort.severity' | translate }}
            </button>
            <button mat-menu-item (click)="setSort('name')">
              {{ 'channel.health.sort.name' | translate }}
            </button>
          </mat-menu>

          <!-- View switch — same pattern as the Kanban board/list toggle -->
          <div class="health-controls__group btn-group trend-button gap-1" role="group"
            [attr.aria-label]="'channel.health.view.label' | translate">
            <button type="button" class="btn"
              [class.btn-primary]="viewMode === 'cards'"
              [class.btn-outline-secondary]="viewMode !== 'cards'"
              [matTooltip]="'channel.health.view.cards' | translate"
              (click)="setViewMode('cards')">
              <i class="mdi mdi-view-grid-outline"></i>
            </button>
            <button type="button" class="btn"
              [class.btn-primary]="viewMode === 'table'"
              [class.btn-outline-secondary]="viewMode !== 'table'"
              [matTooltip]="'channel.health.view.table' | translate"
              (click)="setViewMode('table')">
              <i class="mdi mdi-format-list-bulleted"></i>
            </button>
          </div>

          <div class="health-controls__sync">
            @if (lastSyncLabel()) {
              <span class="health-controls__sync-label">{{ lastSyncLabel() }}</span>
            }
            <button type="button" class="btn btn-outline-primary" (click)="refreshAll()" [disabled]="refreshing">
              @if (!refreshing) {
                <i class="ri-refresh-line"></i>
                {{ 'channel.health.refresh' | translate }}
              } @else {
                <mat-spinner [diameter]="20" mode="indeterminate" />
              }
            </button>
          </div>

          @if (canManage()) {
            <button class="btn btn-success fw-bold health-controls__add" (click)="add()">
              {{ 'channel.list.addButton' | translate }}
            </button>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Summary header — counts per classification, click to filter (RF-03); same stat card as the dashboard -->
  <div class="row health-kpis">
    <div class="col-6 col-md kpi-col">
      <div class="kpi-select" [class.kpi-select--active]="filterClassification === null"
        (click)="toggleFilter(null)" role="button" tabindex="0">
        <card-graphic-small-legacy-component
          [label]="formatCount(counts.total)"
          [total]="'channel.health.kpi.total' | translate" />
      </div>
    </div>
    <div class="col-6 col-md kpi-col">
      <div class="kpi-select" [class.kpi-select--active]="filterClassification === classification.CRITICAL"
        (click)="toggleFilter(classification.CRITICAL)" role="button" tabindex="0">
        <card-graphic-small-legacy-component
          [label]="formatCount(counts.critical)"
          [total]="'channel.health.kpi.critical' | translate" />
      </div>
    </div>
    <div class="col-6 col-md kpi-col">
      <div class="kpi-select" [class.kpi-select--active]="filterClassification === classification.ATTENTION"
        (click)="toggleFilter(classification.ATTENTION)" role="button" tabindex="0">
        <card-graphic-small-legacy-component
          [label]="formatCount(counts.attention)"
          [total]="'channel.health.kpi.attention' | translate" />
      </div>
    </div>
    <div class="col-6 col-md kpi-col">
      <div class="kpi-select" [class.kpi-select--active]="filterClassification === classification.HEALTHY"
        (click)="toggleFilter(classification.HEALTHY)" role="button" tabindex="0">
        <card-graphic-small-legacy-component
          [label]="formatCount(counts.healthy)"
          [total]="'channel.health.kpi.healthy' | translate" />
      </div>
    </div>
    <div class="col-6 col-md kpi-col">
      <div class="kpi-select" [class.kpi-select--active]="filterClassification === classification.NO_DATA"
        (click)="toggleFilter(classification.NO_DATA)" role="button" tabindex="0">
        <card-graphic-small-legacy-component
          [label]="formatCount(counts.noData)"
          [total]="'channel.health.kpi.noData' | translate" />
      </div>
    </div>
  </div>

  @if (loadingContent) {
    <content-loading-list></content-loading-list>
  }

  @if (!loadingContent && counts.total === 0) {
    <empty-records-component [labelButton]="'channel.list.addButton' | translate" [buttonClass]="'btn btn-info fw-bold'"
      [subMessage]="'channel.list.emptySubMessage' | translate" [message]="'channel.list.emptyMessage' | translate"
      [icon]="'ri-message-3-line'" (emitEvent)="add()" />
  }

  <!-- Cards view -->
  @if (!loadingContent && counts.total > 0 && viewMode === 'cards') {
    <div class="row">
      @for (item of visibleChannels; track item._id) {
        <div class="col-xs-12 col-sm-6 col-md-4">
          <div class="card channel-card" [attr.data-channel-type]="item.type">
            <div class="card-body">
              <a href="javascript:void(0)" [routerLink]="['/channel/view', item._id]">
                <div class="d-flex justify-content-between align-items-start">
                  <span class="badge campaign-badge" [ngClass]="badgeClass(item)">
                    {{ badgeLabel(item) }}
                  </span>
                  <span
                    class="badge campaign-badge status-badge"
                    [ngClass]="{
                      'is-ok status-badge--connected': item.connected,
                      'is-err status-badge--disconnected': !item.connected,
                    }"
                  >
                    <i
                      class="status-dot"
                      [ngClass]="{
                        'status-dot--connected': item.connected,
                        'status-dot--disconnected': !item.connected
                      }"></i>
                    {{ (item.connected ? 'channel.channelConfig.connected' : 'channel.channelConfig.disconnected') | translate }}
                  </span>
                </div>

                <div class="channel-card__icon">
                  @if (item.type === cloudAPI) {
                    <div class="icon-wrapper icon-wrapper--official">
                      <img
                        src="../../../assets/images/icon-whatsapp.svg"
                        [alt]="'channel.channelConfig.whatsappBusinessAlt' | translate"
                        width="48"
                        height="48"
                      />
                      <span class="official-badge" [class.official-badge--hybrid]="hybridBadges(item).hybrid">
                        <i class="mdi mdi-check-decagram"></i>
                      </span>
                    </div>
                  } @else if (item.type === zAPI) {
                    <div class="icon-wrapper icon-wrapper--unofficial">
                      <img
                        src="../../../assets/images/icon-whatsapp.svg"
                        [alt]="'channel.channelConfig.whatsappUnofficialAlt' | translate"
                        width="48"
                        height="48"
                      />
                    </div>
                  } @else if (item.type === instagramAPI) {
                    <div class="icon-wrapper icon-wrapper--unofficial">
                      <img src="../../../assets/images/icon-instagram.png" alt="Instagram" width="48" height="48" />
                    </div>
                  } @else if (item.type === messengerAPI) {
                    <div class="icon-wrapper icon-wrapper--unofficial">
                      <img src="../../../assets/images/icon-messenger.png" alt="Messenger" width="48" height="48" />
                    </div>
                  } @else if (item.type === rcsAPI) {
                    <div class="icon-wrapper icon-wrapper--unofficial">
                      <img src="/assets/images/Google-Messages.png" alt="RCS" height="48" />
                    </div>
                  } @else if (item.type === voipAPI) {
                    <div class="icon-wrapper icon-wrapper--unofficial">
                      <i class="mdi mdi-phone-voip font-48 text-primary"></i>
                    </div>
                  } @else if (item.type === webchatAPI) {
                    <div class="icon-wrapper icon-wrapper--unofficial">
                      <i class="mdi mdi-chat font-48 text-primary"></i>
                    </div>
                  }
                </div>

                <div class="channel-card__content">
                  <h5 class="channel-card__title">{{ item.name }}</h5>
                  <p class="channel-card__type">
                    {{ item.creditLine ? ('channel.channelForm.cloudApiCreditLineOption' | translate) : getTranslatedTypeChannel(item.type) }}
                  </p>
                  @if (hybridBadges(item).hybrid) {
                    <!-- Hybrid channel (SPEC-2026-10-whatsapp-hybrid-channel RF-05/RF-22) -->
                    <p class="channel-card__type d-flex justify-content-center flex-wrap gap-1">
                      <span class="badge bg-soft-primary text-primary">
                        <i class="ri-git-branch-line me-1"></i>{{ 'channel.channelList.hybridBadge' | translate }}
                      </span>
                      @if (hybridBadges(item).sessionDisconnected) {
                        <span class="badge bg-soft-danger text-danger">
                          <i class="mdi mdi-link-variant-off me-1"></i>{{ 'channel.channelList.hybridSessionDisconnectedBadge' | translate }}
                        </span>
                      }
                    </p>
                  }
                  <p class="channel-card__type">
                    {{ item?.identifier ? item.identifier : ('channel.channelConfig.noIdentifier' | translate) }}
                  </p>

                  @if (qualityLabel(item)) {
                    <p class="channel-card__quality">
                      <span class="badge campaign-badge" [ngClass]="qualityClass(item)">
                        {{ 'channel.health.quality.label' | translate }}: {{ qualityLabel(item) }}
                      </span>
                    </p>
                  }
                </div>
              </a>

              <!-- Main pending issue (RF-08) / "Sem dados" reason (RF-10) -->
              @if (pendingIssueOf(item); as issue) {
                <div class="pending-issue" [ngClass]="'pending-issue--' + classificationOf(item).toLowerCase()">
                  <span class="pending-issue__category">{{ issue.category }}</span>
                  <span class="pending-issue__title">{{ issue.title }}</span>
                  <a href="javascript:void(0)" class="pending-issue__link" [routerLink]="['/channel/view', item._id]">
                    {{ 'channel.health.pending.view' | translate }}
                  </a>
                </div>
              } @else if (isCloud(item) && classificationOf(item) === classification.HEALTHY) {
                <div class="pending-issue pending-issue--none">
                  {{ 'channel.health.pending.none' | translate }}
                </div>
              } @else if (noDataReason(item)) {
                <div class="pending-issue pending-issue--none">
                  {{ noDataReason(item) }}
                </div>
              }

              <div class="channel-card__freshness">
                {{ freshnessOf(item) }}
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <!-- Table view (same list, RF-05; per-row freshness, RF-09) — system standard table -->
  @if (!loadingContent && counts.total > 0 && viewMode === 'table') {
    <div class="card">
      <div class="card-body table-responsive">
        <table class="table table-hover table-centered mb-0 health-table">
          <thead>
            <tr>
              <th class="text-primary-emphasis">{{ 'channel.health.table.channel' | translate }}</th>
              <th class="text-primary-emphasis">{{ 'channel.health.table.type' | translate }}</th>
              <th class="text-primary-emphasis">{{ 'channel.health.table.state' | translate }}</th>
              <th class="text-primary-emphasis">{{ 'channel.health.table.quality' | translate }}</th>
              <th class="text-primary-emphasis">{{ 'channel.health.table.pending' | translate }}</th>
              <th class="text-primary-emphasis">{{ 'channel.health.table.updated' | translate }}</th>
              <th class="text-primary-emphasis">{{ 'channel.health.table.connection' | translate }}</th>
              <th>
                <span class="sr-only"></span>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (item of visibleChannels; track item._id) {
              <tr class="health-table__row" (click)="view(item)">
                <td>
                  <div class="d-flex flex-column">
                    <span class="fw-bold">{{ item.name }}</span>
                    <small class="text-muted">{{ item?.identifier || ('channel.channelConfig.noIdentifier' | translate) }}</small>
                  </div>
                </td>
                <td>
                  {{ item.creditLine ? ('channel.channelForm.cloudApiCreditLineOption' | translate) : getTranslatedTypeChannel(item.type) }}
                  @if (hybridBadges(item).hybrid) {
                    <span class="badge bg-soft-primary text-primary ms-1">
                      <i class="ri-git-branch-line me-1"></i>{{ 'channel.channelList.hybridBadge' | translate }}
                    </span>
                    @if (hybridBadges(item).sessionDisconnected) {
                      <span class="badge bg-soft-danger text-danger ms-1">
                        <i class="mdi mdi-link-variant-off me-1"></i>{{ 'channel.channelList.hybridSessionDisconnectedBadge' | translate }}
                      </span>
                    }
                  }
                </td>
                <td>
                  <span class="badge campaign-badge" [ngClass]="badgeClass(item)">{{ badgeLabel(item) }}</span>
                </td>
                <td>
                  @if (qualityLabel(item)) {
                    <span class="badge campaign-badge" [ngClass]="qualityClass(item)">{{ qualityLabel(item) }}</span>
                  } @else {
                    <span class="text-muted">—</span>
                  }
                </td>
                <td class="health-table__pending">
                  @if (pendingIssueOf(item); as issue) {
                    <span [title]="issue.title">{{ issue.title }}</span>
                  } @else if (isCloud(item) && classificationOf(item) === classification.HEALTHY) {
                    <span class="text-muted">{{ 'channel.health.pending.none' | translate }}</span>
                  } @else {
                    <span class="text-muted">{{ noDataReason(item) }}</span>
                  }
                </td>
                <td class="text-muted">{{ freshnessOf(item, 'short') }}</td>
                <td>
                  <span
                    class="badge campaign-badge status-badge"
                    [ngClass]="{
                      'is-ok status-badge--connected': item.connected,
                      'is-err status-badge--disconnected': !item.connected,
                    }"
                  >
                    <i class="status-dot" [ngClass]="{
                      'status-dot--connected': item.connected,
                      'status-dot--disconnected': !item.connected
                    }"></i>
                    {{ (item.connected ? 'channel.channelConfig.connected' : 'channel.channelConfig.disconnected') | translate }}
                  </span>
                </td>
                <td class="text-end">
                  <button
                    class="btn btn-dropdown-list"
                    type="button"
                    [matMenuTriggerFor]="rowMenu"
                    [attr.aria-label]="'channel.health.table.actions' | translate"
                    (click)="$event.stopPropagation()"
                    (mousedown)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                  >
                    <i class="mdi mdi-dots-vertical"></i>
                  </button>
                  <mat-menu #rowMenu="matMenu">
                    <button mat-menu-item type="button" (click)="view(item); $event.stopPropagation()">
                      {{ 'channel.health.pending.view' | translate }}
                    </button>
                  </mat-menu>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
}
